import { TaskCardBase, TaskCardBaseProps } from "~components";
import type { TaskComputed } from "~onboarding/utils";
import { formatAsMonthAndDay } from "~utils";

/***** Types *****/
type TaskCardProps = Omit<TaskComputed, "steps"> & {
  /**
   * Handler when clicking the Card
   */
  onClick?: () => void;
  /**
   * Boolean to determine whether Task is locked currently
   */
  isDisabled?: boolean;
};

/**
 * Component | TaskCard
 *
 * Renders a TaskCard with the given props.
 */
export function TaskCard(props: TaskCardProps) {
  return <TaskCardBase {...taskCardBaseProps(props)} />;
}

/***** Helpers *****/
function taskCardBaseProps(props: TaskCardProps): TaskCardBaseProps {
  const {
    icon,
    durationTimeText,
    name,
    progress,
    status,
    updatedAt,
    isDisabled,
    onClick,
    isOptional,
  } = props;

  const progressPercent = progress * 100;
  const baseProps = {
    title: name,
    description: durationTimeText,
    onClick: onClick,
  };

  if (isDisabled) {
    return {
      ...baseProps,
      progressIcon: {
        icon: "Lock",
        color: "SLATE",
        percentage: progressPercent,
      },
      // Show optional chip even when disabled
      ...(isOptional && {
        chip: {
          color: "SLATE",
          label: "Optional",
        },
      }),
    };
  }

  if (status === "UNDER_REVIEW") {
    return {
      ...baseProps,
      progressIcon: {
        icon: "Hourglass",
        color: "PURPLE",
        percentage: progressPercent,
      },
      // Note: Status chips take priority over optional chips
      chip: {
        color: "PURPLE",
        // Forcing `updateAt` since if there is a `status`, there must be an `updatedAt`.
        label: `Submitted on ${formatAsMonthAndDay(updatedAt!)}`,
      },
    };
  }

  if (status === "ACTION_REQUIRED") {
    return {
      ...baseProps,
      progressIcon: {
        icon: "Warning",
        color: "YELLOW",
        percentage: progressPercent,
      },
      // Note: Status chips take priority over optional chips
      chip: {
        color: "YELLOW",
        label: "Action Required",
      },
    };
  }

  if (status === "COMPLETED") {
    return {
      ...baseProps,
      progressIcon: {
        icon: "Check Circle",
        color: "GREEN",
        percentage: progressPercent,
      },
      // Show optional chip for completed optional tasks
      ...(isOptional && {
        chip: {
          color: "SLATE",
          label: "Optional",
        },
      }),
    };
  }

  return {
    ...baseProps,
    progressIcon: {
      icon: icon,
      color: "BLUE",
      percentage: progressPercent,
    },
    // Show optional chip for in-progress optional tasks
    ...(isOptional && {
      chip: {
        color: "GREEN",
        label: "Optional",
      },
    }),
  };
}
